import React, { ChangeEvent, useState } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { IModalAttachment } from '@entities/attachment/model/IModalAttachment.interface';
import { IAttachmentResponse } from '@entities/task/model/types/tasks';
import { TModalMode } from '@shared/model/types/modalMode.type';
import { generateId } from 'shared/helpers';

import { DnD } from './DnD';
import cls from './ModalFilesUploader.module.css';
import { processFile } from '../lib';
import { useUploadFiles } from '../lib/hooks/useUploadFile';

interface IModalFilesUploaderProps<TServerAttachment extends IAttachmentResponse = never> {
    attachments: (IModalAttachment | TServerAttachment)[];
    setAttachments: React.Dispatch<
        React.SetStateAction<(IModalAttachment | TServerAttachment)[]>
    >;
    modalMode?: TModalMode;
    onEnablePreviewModal: (enablePreview: boolean, fileId: string) => void;
    previewEnable: boolean;
    setDeleteConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
    setFileIdToDeleteFromList: React.Dispatch<React.SetStateAction<string>>;
    isDisabled: boolean;
    canDelete?: boolean;
    canUpload?: boolean;
    isTaskChecked?: boolean;
}

export const ModalFilesUploader = <TServerAttachment extends IAttachmentResponse = never,>({
    attachments,
    setAttachments,
    canDelete = true,
    canUpload = true,
    onEnablePreviewModal,
    previewEnable,
    setDeleteConfirmModal,
    setFileIdToDeleteFromList,
    isDisabled,
    isTaskChecked = false,
}: IModalFilesUploaderProps<TServerAttachment>) => {
    const { t } = useTranslation();
    const [isFileOver, setIsFileOver] = useState(false);

    const onAddFiles = async (files: File[]) => {
        const tempAttachments = files.map((file) => ({
            file,
            isLoading: true,
            id: String(generateId()),
        }));

        setAttachments((prev) => [...prev, ...tempAttachments]);

        const processedFiles = await Promise.all(
            files.map((file) => {
                const temp = tempAttachments.find((ta) => ta.file.name === file.name);
                const fileId = temp ? temp.id : String(generateId());
                return processFile(file, fileId);
            })
        );

        setAttachments((prev) =>
            prev.map(
                (attachment) => processedFiles.find((pf) => pf.id === attachment.id) || attachment
            )
        );
    };

    const uploadFilesHandler = useUploadFiles(onAddFiles, attachments.length || 0);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!canUpload || isDisabled) return;

        if (attachments.length > 0) return;
        setIsFileOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const { relatedTarget } = e;

        if (!e.currentTarget.contains(relatedTarget as Node)) {
            setIsFileOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canUpload || isDisabled) return;

        setIsFileOver(false);
        const uploadFiles = Array.from(e.dataTransfer.files);

        uploadFilesHandler(uploadFiles);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!canUpload || isDisabled) return;

        setIsFileOver(true);
    };
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!canUpload || isDisabled) return;

        const uploadFiles = Array.from(e.target.files);

        uploadFilesHandler(uploadFiles);
        e.target.value = '';
    };

    const handleFileItemClick = (e: React.SyntheticEvent, fileId: string) => {
        e.stopPropagation();
        onEnablePreviewModal(!previewEnable, fileId);
    };

    return (
        <div
            className={cn(
                cls.Container,
                attachments.length > 0 && cls.hasAttachments,
                isFileOver && cls.dragOver,
                isDisabled && cls.disabled
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <DnD
                attachments={attachments}
                isFileOver={isFileOver}
                onDropHandler={handleDrop}
                onDragEnterHandler={handleDragEnter}
                onInputChange={handleInputChange}
                isDisabled={attachments.length > 0 || isDisabled}
                isDisabledForAdd={isDisabled}
                isTaskChecked={isTaskChecked}
                handleDelete={() => setDeleteConfirmModal((prev) => !prev)}
                canDelete={canDelete}
                setFileIdToDeleteFromList={setFileIdToDeleteFromList}
                handleFileItemClick={handleFileItemClick}
            />
            {isFileOver && attachments.length > 0 && !isDisabled && (
                <span className={cls.dragOverTip}>
                    {t('Tasks.ModalFileUploader.dragOverTip.title')}
                </span>
            )}
        </div>
    );
};
